import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { LayoutProvider } from '../context/LayoutContext';
import { CrmPageSurface } from './ui';

const LISTING_ROUTES = new Set([
  '/leads',
  '/products/list',
  '/categories',
  '/attributes',
  '/quotations',
  '/orders',
  '/proforma-invoices',
  '/invoices',
  '/payments',
  '/payments/history',
]);

const isPageBuilderRoute = (pathname) => /^\/website\/page\/[^/]+/.test(pathname)
  || /^\/storefronts\/[^/]+\/experience\/(?:content\/)?page\/[^/]+/.test(pathname);
const isQuotationBuilderRoute = (pathname) => pathname === '/quotation-create' || /^\/quotation\/create\/[^/]+/.test(pathname);
const isOperationalBoardRoute = (pathname) => pathname === '/kots' || pathname === '/deliveries';
const isSingleRecordRoute = (pathname) => /^\/(leads|quotations|invoices|proforma-invoices)\/[^/]+$/.test(pathname);

const Layout = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' ? true : window.innerWidth > 767);
  const autoCollapsed = useRef(false);
  const wasImmersive = useRef(false);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const immersiveBuilder = isPageBuilderRoute(location.pathname) || isQuotationBuilderRoute(location.pathname) || isOperationalBoardRoute(location.pathname);

  useEffect(() => {
    const enteringBuilder = immersiveBuilder && !wasImmersive.current;
    const leavingBuilder = !immersiveBuilder && wasImmersive.current;

    if (enteringBuilder && sidebarOpen) {
      autoCollapsed.current = true;
      setSidebarOpen(false);
    } else if (leavingBuilder && autoCollapsed.current) {
      autoCollapsed.current = false;
      setSidebarOpen(true);
    }

    wasImmersive.current = immersiveBuilder;
  }, [immersiveBuilder, sidebarOpen]);

  const contextValue = useMemo(() => ({ sidebarOpen, toggleSidebar }), [sidebarOpen]);
  const isListingRoute = LISTING_ROUTES.has(location.pathname);
  const isRecordRoute = isSingleRecordRoute(location.pathname);

  return (
    <LayoutProvider value={contextValue}>
      <div className={`crm-app-shell ${sidebarOpen ? '' : 'crm-app-shell--sidebar-collapsed'} ${immersiveBuilder ? 'crm-app-shell--immersive-builder' : ''}`}>
        <Topbar />
        <div className={`sidebar-container ${sidebarOpen ? '' : 'collapsed'}`}>
          <Sidebar />
        </div>
        <main className={`crm-workspace ${immersiveBuilder ? 'crm-workspace--builder' : ''}`}>
          <CrmPageSurface
            fullBleed={immersiveBuilder}
            className={[isListingRoute ? 'crm-page-surface--listing' : '', isRecordRoute ? 'crm-page-surface--record' : ''].filter(Boolean).join(' ')}
            data-route={location.pathname}
          >
            <Outlet />
          </CrmPageSurface>
        </main>
      </div>
    </LayoutProvider>
  );
};

export default Layout;
